import { IconName } from '@economic/gaudi';
import { t } from '../../components/l10n/l10n';

export type Notification = {
    id: string;
    type: NotificationType;
    createdAt: Date;
    data: object;
    content?: {
        text?: string;
        href?: string;
    };
    read: boolean;
    groupId?: string;
};

export enum NotificationType {
    Custom = 0,
    DocumentReceived = 1,
    BankImportCompleted = 2,
    NoteFollowUpDateReached = 3,
    SalesInvoiceOverdue = 4,
    RequestedDocumentUploaded = 6,
    DocumentRequestRejected = 8,
    PurchaseInvoiceOverdue = 10,
    SalesInvoiceSentError = 11,
    NewInvoiceFromEconomic = 12,
}

export type NotificationDetails = {
    name: string;
    icon: IconName;
    textId?: string;
    textIdPlural?: string;
    getTextId?: (notification: Notification) => string;
    getRoute?: (notification: Notification) => string;
};

export const notificationDetails: Record<NotificationType, NotificationDetails> = {
    [NotificationType.Custom]: {
        name: `NOTIFICATIONS_TYPES_CUSTOM_NAME`,
        icon: 'e-copedia',
    },
    [NotificationType.DocumentReceived]: {
        name: `NOTIFICATIONS_TYPES_DOCUMENT_RECEIVED_NAME`,
        icon: 'inbox',
        getRoute: _ => '/inbox',
        textId: 'NOTIFICATIONS_TYPES_DOCUMENT_RECEIVED_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_DOCUMENT_RECEIVED_PLURAL_INFO',
    },
    [NotificationType.BankImportCompleted]: {
        name: `NOTIFICATIONS_TYPES_BANK_IMPORT_COMPLETED_NAME`,
        icon: 'accounting',
        getRoute: _ => '/accounting/bank/reconciliation',
        textId: 'NOTIFICATIONS_TYPES_BANK_IMPORT_COMPLETED_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_BANK_IMPORT_COMPLETED_PLURAL_INFO',
    },
    [NotificationType.NoteFollowUpDateReached]: {
        name: `NOTIFICATIONS_TYPES_NOTE_FOLLOW_UP_NAME`,
        icon: 'note-follow-up',
        getRoute: _ => '/user/notes/follow-ups',
        textId: 'NOTIFICATIONS_TYPES_NOTE_FOLLOW_UP_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_NOTE_FOLLOW_UP_PLURAL_INFO',
    },
    [NotificationType.SalesInvoiceOverdue]: {
        name: `NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_NAME`,
        icon: 'document-time',
        getRoute: _ => '/sales/invoicing/reminders',
        getTextId: (notification: Notification) => {
            if (!notification.data || (notification.data as any).invoiceCount === 1) {
                return 'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_INFO';
            }
            return 'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_PLURAL_INFO';
        },
        textIdPlural: 'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_GROUPED_INFO',
    },
    [NotificationType.RequestedDocumentUploaded]: {
        name: `NOTIFICATIONS_TYPES_REQUESTED_DOCUMENT_UPLOADED_NAME`,
        icon: 'document-received',
        textId: 'NOTIFICATIONS_TYPES_REQUESTED_DOCUMENT_UPLOADED_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_REQUESTED_DOCUMENT_UPLOADED_PLURAL_INFO',
        getRoute: (notification: Notification) => {
            const data = notification.data as any;
            if (data.AutoApproveDocuments === true) {
                return `/accounting/journals/${data.JournalId}`;
            } else {
                return `/accounting/document-requests/review/${data.DocumentRequestId}`;
            }
        },
    },
    [NotificationType.DocumentRequestRejected]: {
        name: `NOTIFICATIONS_TYPES_DOCUMENT_REQUEST_REJECTED_NAME`,
        icon: 'document-rejected-request',
        getRoute: (notification: Notification) => {
            const data = notification.data as any;
            return `/accounting/document-requests/entries-without-documents?fromVoucher=${data.VoucherNumber}&toVoucher=${data.VoucherNumber}`;
        },
        textId: 'NOTIFICATIONS_TYPES_DOCUMENT_REQUEST_REJECTED_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_DOCUMENT_REQUEST_REJECTED_PLURAL_INFO',
    },
    [NotificationType.PurchaseInvoiceOverdue]: {
        name: `NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_NAME`,
        icon: 'document-time',
        getRoute: _ => '/reports/suppliers/aged-creditors',
        getTextId: (notification: Notification) => {
            if (!notification.data || (notification.data as any).invoiceCount === 1) {
                return 'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_INFO';
            }
            return 'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_PLURAL_INFO';
        },
        textIdPlural: 'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_GROUPED_INFO',
    },
    [NotificationType.SalesInvoiceSentError]: {
        name: `NOTIFICATIONS_TYPES_SALES_INVOICE_SENT_ERROR_NAME`,
        icon: 'close',
        getRoute: _ => '/sales/invoicing/sent',
        textId: 'NOTIFICATIONS_TYPES_SALES_INVOICE_SENT_ERROR_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_SALES_INVOICE_SENT_ERROR_GROUPED_INFO',
    },
    [NotificationType.NewInvoiceFromEconomic]: {
        name: `NOTIFICATIONS_TYPES_NEW_INVOICE_FROM_ECONOMIC_NAME`,
        icon: 'document',
        getRoute: _ => '/settings/subscription/status',
        textId: 'NOTIFICATIONS_TYPES_NEW_INVOICE_FROM_ECONOMIC_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_NEW_INVOICE_FROM_ECONOMIC_GROUPED_INFO',
    },
};

const replaceText = (textId: string, data: object) => {
    let text = t(textId);

    if (data) {
        for (let key of Object.keys(data)) {
            text = text.replace(`[${key.toUpperCase()}]`, data[key]);
        }
    }

    return text;
};

export const getNotificationText = (notification: Notification, groupTotal: number | undefined = undefined) => {
    const details = notificationDetails[notification.type];

    let text;

    if (notification.content?.text) {
        text = notification.content?.text;
    } else if (groupTotal && details.textIdPlural) {
        text = replaceText(details.textIdPlural, {
            TOTAL: groupTotal,
        });
    } else if (details.getTextId) {
        text = replaceText(details.getTextId(notification), notification.data);
    } else if (details.textId) {
        text = replaceText(details.textId, notification.data);
    }

    return text;
};

export const getNotificationClickHandler = (notification: Notification, navigate) => {
    const details = notificationDetails[notification.type];
    const url = notification.content?.href || (details.getRoute && details.getRoute(notification));

    let handleClick;

    if (url) {
        handleClick = () => {
            if (url.startsWith('http')) {
                window.open(url, '__blank');
            } else {
                navigate(url);
            }
        };
    }

    return handleClick;
};
